import {
    getAllRoleAssignments,
    stringifyLosslessJson,
    type TransactionLogDisplayItem,
    type TransactionLogListItem,
    type UnrecognizedTransactionLogListItem,
} from '@pillar-portal/api-types';
import { useMemo, useState, type ReactElement } from 'react';
import { z } from 'zod';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { Input } from '@/components/ui/input';
import {
    ForensicNetworkDetails,
    ForensicRoleDetails,
    ForensicUserDetails,
} from '@/components/ui/forensic-record-details';
import { SectionErrorBoundary } from '@/components/ui/section-error-boundary';
import { useAuthStore } from '@/store/auth';
import type {
    InfiniteScrollListFieldPredicateDescriptor,
    InfiniteScrollListPhysicalJsonStructurePredicateDescriptor,
} from '@/components/ui/infinite-scroll-list.types';
import { auditApi } from '@/lib/pillarApi';
import {
    fetchForensicOrganizationOptionsPage,
    fetchForensicSessionOptionsPage,
    fetchForensicUserOptionsPage,
    ForensicOptionPageSize,
} from '@/lib/forensic-option-fetchers';
import {
    TransactionLogTableNames,
    TransactionLogSortDirections,
    TransactionLogSortFields,
    type TransactionLogSort,
    type TransactionLogSortDirection,
    type TransactionLogSortField,
    type ListTransactionLogDisplayPageResponse,
    type TransactionLogSearchRequest,
} from '@pillar-portal/api-types';
import { HistoricalCredentialDisclosure } from './HistoricalCredentialDisclosure';

interface TransactionLogRowProps {
    readonly canRevealHistoricalCredential: boolean;
    readonly disclosureIdentity: string;
    readonly item: TransactionLogListItem;
}

const AuditLogFieldPredicateDescriptors: readonly InfiniteScrollListFieldPredicateDescriptor[] = [
    {
        field: 'operation',
        label: 'Operation',
        operator: 'in',
        valueType: 'string',
        options: [
            { value: 'INSERT', label: 'INSERT' },
            { value: 'UPDATE', label: 'UPDATE' },
            { value: 'DELETE', label: 'DELETE' },
            { value: 'LOGIN', label: 'LOGIN' },
            { value: 'MFA_SUCCESS', label: 'MFA_SUCCESS' },
            {
                value: 'ROLE_ASSIGNMENT_ACTIVATED',
                label: 'ROLE_ASSIGNMENT_ACTIVATED',
            },
            { value: 'LOGOUT_SUCCESS', label: 'LOGOUT_SUCCESS' },
            { value: 'SESSION_EXPIRED', label: 'SESSION_EXPIRED' },
            { value: 'IMPERSONATION_STARTED', label: 'IMPERSONATION_STARTED' },
            { value: 'IMPERSONATION_ENDED', label: 'IMPERSONATION_ENDED' },
            {
                value: 'CREDENTIAL_HISTORICAL_REVEAL_SUCCEEDED',
                label: 'CREDENTIAL_HISTORICAL_REVEAL_SUCCEEDED',
            },
            {
                value: 'CREDENTIAL_HISTORICAL_REVEAL_FAILED',
                label: 'CREDENTIAL_HISTORICAL_REVEAL_FAILED',
            },
        ],
    },
    {
        field: 'table_name',
        label: 'Table',
        operator: 'in',
        valueType: 'string',
        options: TransactionLogTableNames.map((tableName) => ({
            value: tableName,
            label: tableName,
        })),
    },
    {
        field: 'author',
        label: 'Author',
        operator: 'in',
        valueType: 'string',
        remoteOptions: {
            pageSize: ForensicOptionPageSize,
            fetchPage: fetchForensicUserOptionsPage,
        },
    },
    {
        field: 'organization',
        label: 'Organization',
        operator: 'in',
        valueType: 'string',
        remoteOptions: {
            pageSize: ForensicOptionPageSize,
            fetchPage: fetchForensicOrganizationOptionsPage,
        },
    },
    {
        field: 'session',
        label: 'Session',
        operator: 'in',
        valueType: 'string',
        remoteOptions: {
            pageSize: ForensicOptionPageSize,
            fetchPage: fetchForensicSessionOptionsPage,
        },
    },
];

const AuditLogPhysicalJsonStructurePredicateDescriptors: readonly InfiniteScrollListPhysicalJsonStructurePredicateDescriptor[] =
    [{ field: 'row_details', label: 'Row details' }];

const AuditLogSortFieldSchema: z.ZodType<TransactionLogSortField> =
    z.enum(TransactionLogSortFields);
const AuditLogSortDirectionSchema: z.ZodType<TransactionLogSortDirection> = z.enum(
    TransactionLogSortDirections
);

const AuditLogSortFields: ReadonlyArray<{
    readonly value: TransactionLogSortField;
    readonly label: string;
}> = [
    { value: 'transaction_log_ref', label: 'Record' },
    { value: 'ts', label: 'Timestamp' },
    { value: 'operation', label: 'Operation' },
    { value: 'table_name', label: 'Table' },
    { value: 'row_details', label: 'Row details path' },
    { value: 'ip_address', label: 'IP address' },
    { value: 'user_agent', label: 'User agent' },
];

function splitAuditLogJsonPath(rawPath: string): string[] {
    return rawPath
        .split('.')
        .map((pathPart: string) => pathPart.trim())
        .filter((pathPart: string) => pathPart.length > 0);
}

function TransactionLogRow({
    canRevealHistoricalCredential,
    disclosureIdentity,
    item,
}: TransactionLogRowProps): ReactElement {
    return (
        <article
            className="space-y-2 rounded-md border bg-white p-3"
            data-transaction-log-ref={item.transaction_log_ref}
        >
            <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">
                    {'table_name' in item ? `${item.table_name} ${item.operation}` : item.operation}
                </div>
                <div className="text-xs text-gray-600">
                    {item.ts} · Record {item.transaction_log_ref} · Session{' '}
                    {item.session_ref ?? 'Unavailable'}
                </div>
            </div>

            <ForensicUserDetails label="Actor" reference={item.actor} />
            <ForensicRoleDetails roleAssignment={item.effective_role_assignment} />
            <ForensicNetworkDetails ipAddress={item.ip_address} userAgent={item.user_agent} />

            {'row_details' in item ? (
                <div>
                    <div className="mb-1 text-sm font-medium text-gray-700">Row details</div>
                    <pre className="max-h-96 overflow-x-auto overflow-y-auto rounded border bg-gray-50 p-3 text-xs">
                        {stringifyLosslessJson(item.row_details, null, 2)}
                    </pre>
                </div>
            ) : null}
            <HistoricalCredentialDisclosure
                availability={item.credential_historical_reveal}
                canReveal={canRevealHistoricalCredential}
                disclosureIdentity={disclosureIdentity}
                transactionLogReference={item.transaction_log_ref}
                onRevealField={async (request) =>
                    await auditApi.revealHistoricalCredentialField(request)
                }
            />
        </article>
    );
}

function UnrecognizedTransactionLogRow({
    item,
}: {
    readonly item: UnrecognizedTransactionLogListItem;
}): ReactElement {
    return (
        <article
            className="space-y-1 rounded-md border bg-white p-3"
            {...(item.transaction_log_ref === null
                ? {}
                : { 'data-transaction-log-ref': item.transaction_log_ref })}
        >
            <div className="text-sm font-semibold text-gray-900">
                This audit record can't be shown here
            </div>
            <div className="text-xs text-gray-600">
                {item.ts ?? 'Time unavailable'} · Record{' '}
                {item.transaction_log_ref ?? 'reference unavailable'}
            </div>
            <p className="text-sm text-gray-700">
                This page doesn't recognize this kind of record yet. The other records are
                unaffected.
            </p>
        </article>
    );
}

export function AuditLogContent(): ReactElement {
    const user = useAuthStore((state) => state.user);
    const activeRoleAssignmentId = useAuthStore((state) => state.activeRoleAssignmentId);
    const effectiveSessionGeneration = useAuthStore((state) => state.effectiveSessionGeneration);
    const canRevealHistoricalCredential: boolean =
        user !== null &&
        getAllRoleAssignments(user).some(
            (roleAssignment) =>
                roleAssignment.role_assignment_id === activeRoleAssignmentId &&
                roleAssignment.role === 'SUPERADMIN'
        );
    const disclosureIdentity = `${user?.user_uuid ?? 'none'}:${activeRoleAssignmentId ?? 'none'}:${effectiveSessionGeneration.value}`;
    const [sortField, setSortField] = useState<TransactionLogSortField>('transaction_log_ref');
    const [sortDirection, setSortDirection] = useState<TransactionLogSortDirection>('desc');
    const [sortJsonPath, setSortJsonPath] = useState<string>('');
    const sort: TransactionLogSort | undefined = useMemo(() => {
        if (sortField === 'transaction_log_ref' && sortDirection === 'desc') {
            return undefined;
        }
        if (sortField !== 'row_details') {
            return { field: sortField, direction: sortDirection };
        }
        const jsonPath: string[] = splitAuditLogJsonPath(sortJsonPath);
        return jsonPath.length === 0
            ? undefined
            : {
                  field: sortField,
                  direction: sortDirection,
                  json_path: jsonPath,
              };
    }, [sortDirection, sortField, sortJsonPath]);
    const queryKeyPrefix: readonly string[] = useMemo(
        () => ['auditLog', stringifyLosslessJson(sort ?? null)],
        [sort]
    );

    return (
        <InfiniteScrollList<TransactionLogDisplayItem, string, ReadonlyArray<string>>
            pageTitle="Audit Log"
            cardTitle="Transaction Log"
            queryPlaceholder="Search audit log..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={100}
            controlsPlacement="above-search"
            controls={
                <div className="flex flex-wrap items-end gap-3">
                    <label className="flex flex-col gap-1 text-sm font-medium">
                        Sort field
                        <select
                            aria-label="Audit log sort field"
                            className="h-9 min-w-[220px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            value={sortField}
                            onChange={(event) => {
                                setSortField(AuditLogSortFieldSchema.parse(event.target.value));
                                setSortJsonPath('');
                            }}
                        >
                            {AuditLogSortFields.map((field) => (
                                <option key={field.value} value={field.value}>
                                    {field.label}
                                </option>
                            ))}
                        </select>
                    </label>
                    {sortField === 'row_details' ? (
                        <label className="flex flex-col gap-1 text-sm font-medium">
                            Row details path
                            <Input
                                aria-label="Audit log sort JSON path"
                                className="h-9 w-[220px]"
                                placeholder="exact.path.in.stored.version"
                                value={sortJsonPath}
                                onChange={(event) => setSortJsonPath(event.target.value)}
                            />
                        </label>
                    ) : null}
                    <label className="flex flex-col gap-1 text-sm font-medium">
                        Direction
                        <select
                            aria-label="Audit log sort direction"
                            className="h-9 min-w-[160px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            value={sortDirection}
                            onChange={(event) =>
                                setSortDirection(
                                    AuditLogSortDirectionSchema.parse(event.target.value)
                                )
                            }
                        >
                            <option value="asc">Ascending</option>
                            <option value="desc">Descending</option>
                        </select>
                    </label>
                </div>
            }
            fetchPage={async (params) => {
                const request: TransactionLogSearchRequest = {
                    limit: params.limit,
                    ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    ...(params.fieldPredicates !== undefined
                        ? { field_predicates: [...params.fieldPredicates] }
                        : {}),
                    ...(params.jsonPathPredicates !== undefined
                        ? { json_path_predicates: [...params.jsonPathPredicates] }
                        : {}),
                    ...(sort === undefined ? {} : { sort }),
                };
                const response: ListTransactionLogDisplayPageResponse =
                    await auditApi.listTransactionLogDisplayPage(request);
                return { items: response.transaction_log, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.string().min(1).safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected string cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading audit log...',
                emptyLabel: 'No audit log rows found.',
                endOfListLabel: 'End of audit log.',
                genericErrorLabel: 'Failed to load audit log',
            }}
            predicateControls={{
                fieldPredicates: AuditLogFieldPredicateDescriptors,
                jsonPathPredicates: AuditLogPhysicalJsonStructurePredicateDescriptors,
            }}
            renderItems={({ items }) => (
                <div className="space-y-6">
                    <p className="text-sm text-gray-600">
                        Transaction log rows in descending recency order.
                    </p>
                    <div className="space-y-3">
                        {items.map((item, index) =>
                            item.operation === 'UNRECOGNIZED' ? (
                                <UnrecognizedTransactionLogRow
                                    key={item.transaction_log_ref ?? `unrecognized-${index}`}
                                    item={item}
                                />
                            ) : (
                                <TransactionLogRow
                                    key={item.transaction_log_ref}
                                    canRevealHistoricalCredential={canRevealHistoricalCredential}
                                    disclosureIdentity={disclosureIdentity}
                                    item={item}
                                />
                            )
                        )}
                    </div>
                </div>
            )}
        />
    );
}

export default function AuditLogPage(): ReactElement {
    return (
        <ProtectedRoute>
            <MainLayout>
                <SectionErrorBoundary sectionName="Audit Log">
                    <AuditLogContent />
                </SectionErrorBoundary>
            </MainLayout>
        </ProtectedRoute>
    );
}
